/* eslint-disable arrow-parens */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { gatewayRequestAxios } from '../../api/interceptor'
import { GATEWAY_API_PHONE_FACTORS } from '../../util/config'
import { formatPhoneToNational } from '../../util/phoneUtils'

const initialState = {
  currentCountry: '',
  currentCountryPhoneCode: '+',
  userObj: {},
  phoneNumber: '',
  formattedPhone: '',
  phoneField: '',
  status: 'idle',
  error: null,
}
const passdelete = 204
const passsubmit = 201
const passdfetch = 200

export const deletePhoneNumber = createAsyncThunk(
  'userPhone/deletePhoneNumber',
  async (phoneNumber, { rejectWithValue }) => {
    try {
      const response = await gatewayRequestAxios.delete(GATEWAY_API_PHONE_FACTORS, {
        data: { phoneNumber }
      })
      if (response.status === passdelete) {
        return phoneNumber
      }
      throw new Error('Failed to delete phone number')
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

export const submitPhoneNumber = createAsyncThunk(
  'userPhone/submitPhoneNumber',
  // eslint-disable-next-line consistent-return
  async (phoneNumber, { rejectWithValue }) => {
    try {
      const response = await gatewayRequestAxios.post(GATEWAY_API_PHONE_FACTORS,
        {
          phoneNumber
        }
      )
      if (response.status === passsubmit) {
        return phoneNumber
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

export const fetchPhoneNumber = createAsyncThunk(
  'userPhone/fetchPhoneNumber',
  async (_id, { rejectWithValue }) => {
    try {
      const response = await gatewayRequestAxios.get(GATEWAY_API_PHONE_FACTORS)
      if (response.status === passdfetch) {
        return response.data
      }
      throw new Error('Failed to fetch phone factors')
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

const userPhoneSlice = createSlice({
  name: 'userPhone',
  initialState,
  reducers: {
    updatePhoneInfo: (state, action) => {
      const {
        currentCountry, currentCountryPhoneCode, userObj, phoneNumber, formattedPhone, phoneField
      } = action.payload

      let fPhone = formattedPhone
      if (!fPhone) {
        try {
          fPhone = formatPhoneToNational(phoneNumber)
        } catch (err) {
          fPhone = phoneNumber
        }
      }
      state.currentCountry = currentCountry
      state.currentCountryPhoneCode = currentCountryPhoneCode
      state.userObj = userObj
      state.phoneNumber = phoneNumber
      state.formattedPhone = fPhone
      state.phoneField = phoneField
    },
    resetPhoneInfo: (state) => {
      state.currentCountry = ''
      state.currentCountryPhoneCode = ''
      state.userObj = {}
      state.phoneNumber = ''
      state.formattedPhone = ''
      state.phoneField = ''
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deletePhoneNumber.pending, state => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(deletePhoneNumber.fulfilled, state => {
        state.status = 'succeeded'
        state.phoneNumber = ''
        state.formattedPhone = ''
      })
      .addCase(deletePhoneNumber.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload
      })
      .addCase(submitPhoneNumber.pending, state => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(submitPhoneNumber.fulfilled, state => {
        state.status = 'succeeded'
      })
      .addCase(submitPhoneNumber.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload
      })
      .addCase(fetchPhoneNumber.fulfilled, (state, action) => {
        state.status = 'succeeded'
        if (action.payload.length > 0 && (action.payload[0].status === 'ACTIVE')) {
          state.phoneNumber = action.payload[0].phoneNumber
          state.formattedPhone = formatPhoneToNational(action.payload[0].phoneNumber)
        }
      })
      .addCase(fetchPhoneNumber.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload
      })
      .addCase(fetchPhoneNumber.pending, state => {
        state.status = 'loading'
        state.error = null
      })
  }
})

export const { updatePhoneInfo, resetPhoneInfo } = userPhoneSlice.actions
export default userPhoneSlice.reducer

export const selectUserPhone = (state) => state.userPhone
export const selectPhoneField = (state) => state.phoneField
